import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Users, Dumbbell, Calendar, Activity, ArrowRight, LogOut,
  ChevronRight, FileText, X, TrendingUp, Clock, MessageSquare, Flame
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

// ── Sporter detail panel (slide-in) ────────────────────────────────────────────
function SporterPanel({ sporter, token, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!sporter?.id || !token) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/company/sporter-notities/${sporter.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } catch {}
    setLoading(false);
  }, [sporter, token]);

  useEffect(() => { load(); }, [load]);

  const formatDatum = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
  };

  const formatTijd = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-[#0a0a0a] border-l border-white/8 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/5 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black text-black shrink-0"
              style={{ background: GOLD }}>
              {(sporter.naam || "?")[0].toUpperCase()}
            </div>
            <div>
              <p className="text-white font-bold text-sm">{sporter.naam || sporter.email}</p>
              <p className="text-zinc-600 text-xs">{sporter.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="sporter-panel-close"
            className="w-8 h-8 rounded-full bg-white/6 flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/10 transition-all"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-5 py-5">
          {loading ? (
            <div className="space-y-3">
              <div className="h-16 bg-white/5 rounded-xl animate-pulse" />
              <div className="h-24 bg-white/5 rounded-xl animate-pulse" />
            </div>
          ) : !data ? (
            <p className="text-zinc-600 text-sm text-center py-8">Kon gegevens niet laden</p>
          ) : (
            <>
              {/* Statistieken */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-[#111] border border-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <TrendingUp size={12} style={{ color: GOLD }} />
                    <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wide">8 weken</p>
                  </div>
                  <p className="text-2xl font-black text-white">{data.totaal_8_weken ?? data.totaal_trainingen}</p>
                  <p className="text-zinc-700 text-xs mt-0.5">trainingen</p>
                </div>
                <div className="bg-[#111] border border-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Flame size={12} style={{ color: GOLD }} />
                    <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wide">Streak</p>
                  </div>
                  <p className="text-2xl font-black text-white">{data.streak_weken ?? 0}</p>
                  <p className="text-zinc-700 text-xs mt-0.5">weken op rij</p>
                </div>
                <div className="bg-[#111] border border-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Clock size={12} style={{ color: GOLD }} />
                    <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wide">Laatste</p>
                  </div>
                  <p className="text-white font-bold text-sm leading-tight">
                    {data.laatste_training
                      ? new Date(data.laatste_training).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })
                      : "Nog geen"}
                  </p>
                  <p className="text-zinc-700 text-xs mt-0.5">training</p>
                </div>
              </div>

              {/* Voortgangsgrafiek per week */}
              {data.per_week && data.per_week.length > 0 && (
                <div className="bg-[#111] border border-white/5 rounded-xl p-4 mb-5">
                  <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wide mb-3">Trainingen per week</p>
                  <div style={{ height: 90 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.per_week} margin={{ top: 0, right: 0, left: -28, bottom: 0 }}>
                        <XAxis dataKey="week" tick={{ fill: "#52525b", fontSize: 9 }} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fill: "#52525b", fontSize: 9 }} axisLine={false} tickLine={false} />
                        <Tooltip
                          cursor={{ fill: "rgba(201,168,76,0.05)" }}
                          contentStyle={{ background: "#111", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 8, fontSize: 11 }}
                          labelStyle={{ color: "#a1a1aa" }}
                          itemStyle={{ color: GOLD }}
                          formatter={(v) => [`${v} training${v !== 1 ? "en" : ""}`, ""]}
                        />
                        <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                          {data.per_week.map((_, i) => (
                            <Cell key={i} fill={i === data.per_week.length - 1 ? GOLD : "rgba(201,168,76,0.25)"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Schema's */}
              {sporter.schema_count > 0 && (
                <div className="bg-[#111] border border-white/5 rounded-xl px-4 py-3 mb-5 flex items-center gap-3">
                  <Dumbbell size={14} style={{ color: GOLD }} />
                  <p className="text-zinc-400 text-sm">
                    <span className="text-white font-bold">{sporter.schema_count}</span> schema{sporter.schema_count !== 1 ? "'s" : ""} toegewezen
                  </p>
                </div>
              )}

              {/* Notities sectie */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare size={14} style={{ color: GOLD }} />
                  <p className="text-white font-bold text-sm">PT Sessienotities</p>
                  <span className="text-zinc-700 text-xs ml-auto">{data.notities?.length || 0} notities</span>
                </div>

                {!data.notities || data.notities.length === 0 ? (
                  <div className="bg-[#111] border border-white/5 rounded-xl px-4 py-6 text-center">
                    <FileText size={24} className="text-zinc-800 mx-auto mb-2" />
                    <p className="text-zinc-600 text-sm">Nog geen sessienotities</p>
                    <p className="text-zinc-700 text-xs mt-1">Voeg notities toe via PT Modus</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {data.notities.map((n, i) => (
                      <div key={i} className="bg-[#111] border border-white/5 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar size={11} className="text-zinc-600" />
                          <p className="text-zinc-500 text-xs">{formatDatum(n.gestart_op)}</p>
                          {n.gestart_op && (
                            <p className="text-zinc-700 text-xs ml-auto">{formatTijd(n.gestart_op)}</p>
                          )}
                        </div>
                        <p className="text-zinc-300 text-sm leading-relaxed">{n.notitie}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Trainer Dashboard ──────────────────────────────────────────────────────────
export default function TrainerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sporters, setSporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSporter, setSelectedSporter] = useState(null);

  useEffect(() => {
    if (!user?.token) return;
    fetch(`${API}/api/company/sporters`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setSporters(d?.sporters || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user]);

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Navbar */}
      <header className="border-b border-white/5 bg-[#0a0a0a]/95 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#C9A84C]/10 flex items-center justify-center">
              <Dumbbell size={15} className="text-[#C9A84C]" />
            </div>
            <div>
              <p className="text-white font-bold text-sm leading-none">Trainer Dashboard</p>
              <p className="text-zinc-600 text-xs mt-0.5">{user?.name}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="trainer-logout-btn"
            className="flex items-center gap-1.5 text-zinc-500 hover:text-white text-xs transition-colors"
          >
            <LogOut size={14} /> Uitloggen
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Welkom</p>
          <h1 className="text-2xl font-bold text-white">{user?.name}</h1>
          <p className="text-zinc-500 text-sm mt-1">Kies een modus of bekijk sporternotities</p>
        </div>

        {/* Modus knoppen */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <button
            onClick={() => navigate("/groep")}
            data-testid="trainer-groep-modus-btn"
            className="group bg-[#0f0f0f] border border-white/5 hover:border-[#C9A84C]/30 rounded-2xl p-5 text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center mb-3 group-hover:bg-[#C9A84C]/20 transition-colors">
              <Users size={20} className="text-[#C9A84C]" />
            </div>
            <p className="text-white font-bold text-sm">Groep Modus</p>
            <p className="text-zinc-600 text-xs mt-1">Groepstraining begeleiden</p>
            <div className="flex items-center gap-1 mt-3 text-[#C9A84C] text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity">
              Openen <ArrowRight size={11} />
            </div>
          </button>

          <button
            onClick={() => navigate("/pt")}
            data-testid="trainer-pt-modus-btn"
            className="group bg-[#0f0f0f] border border-white/5 hover:border-[#C9A84C]/30 rounded-2xl p-5 text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center mb-3 group-hover:bg-[#C9A84C]/20 transition-colors">
              <Activity size={20} className="text-[#C9A84C]" />
            </div>
            <p className="text-white font-bold text-sm">PT Modus</p>
            <p className="text-zinc-600 text-xs mt-1">1-op-1 personal training</p>
            <div className="flex items-center gap-1 mt-3 text-[#C9A84C] text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity">
              Openen <ArrowRight size={11} />
            </div>
          </button>
        </div>

        {/* Sporters overzicht */}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-[#C9A84C]" />
              <p className="text-white font-bold text-sm">Jouw Sporters</p>
            </div>
            <span className="text-zinc-600 text-xs">{sporters.length} totaal — klik voor notities</span>
          </div>

          {loading ? (
            <div className="px-5 py-8 text-center text-zinc-600 text-sm">Laden...</div>
          ) : sporters.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <Users size={28} className="text-zinc-800 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Nog geen sporters</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {sporters.map((s) => (
                <button
                  key={s.id}
                  data-testid={`trainer-sporter-${s.id}`}
                  onClick={() => setSelectedSporter(s)}
                  className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-white/3 transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
                    <span className="text-[#C9A84C] text-xs font-black">
                      {(s.naam || s.email || "?")[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{s.naam || "—"}</p>
                    <p className="text-zinc-600 text-xs truncate">{s.email}</p>
                  </div>
                  {s.schema_count > 0 && (
                    <div className="flex items-center gap-1 text-zinc-600 text-xs shrink-0">
                      <Calendar size={11} />
                      {s.schema_count}
                    </div>
                  )}
                  <div className="flex items-center gap-1 text-zinc-700 text-xs shrink-0">
                    <FileText size={11} />
                    <span>notities</span>
                  </div>
                  <ChevronRight size={14} className="text-zinc-700 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sporter detail panel */}
      {selectedSporter && (
        <SporterPanel
          sporter={selectedSporter}
          token={user?.token}
          onClose={() => setSelectedSporter(null)}
        />
      )}
    </div>
  );
}
